const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display:inline-block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    vertical-align: middle;
  }

  .badge{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    white-space: nowrap;
    user-select: none;
    font-weight: var(--ctl-font-weight-extrabold);
    letter-spacing: var(--ctl-tracking-normal);
    border-radius: var(--r);
    border: var(--ctl-border-width-thin) solid transparent;
  }

  .icon{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    line-height: 0;
    color: currentColor;
    opacity: var(--ctl-opacity-icon);
  }

  /* sizes */
  :host([size="sm"]) .badge{
    font-size: var(--ctl-font-size-xs);
    padding: var(--ctl-badge-padding-sm);
    --r: var(--ctl-radius-sm);
  }

  :host(:not([size]), [size="md"]) .badge{
    font-size: var(--ctl-font-size-sm);
    padding: var(--ctl-badge-padding-md);
    --r: var(--ctl-radius-md);
  }

  /* pill */
  :host([pill]) .badge{
    border-radius: var(--ctl-radius-full);
    padding-left: var(--ctl-space-2);
    padding-right: var(--ctl-space-2);
  }

  /* determine "ink" color from intent */
  :host([intent="neutral"]) { --ink: var(--ctl-color-muted); }
  :host([intent="info"])    { --ink: var(--ctl-color-info); }
  :host([intent="success"]) { --ink: var(--ctl-color-success); }
  :host([intent="warning"]) { --ink: var(--ctl-color-warning); }
  :host([intent="danger"])  { --ink: var(--ctl-color-danger); }
  :host(:not([intent]))     { --ink: var(--ctl-color-muted); }

  /* variants */
  :host([variant="solid"]) .badge{
    background: var(--ink);
    color: var(--ctl-color-white);
    border-color: color-mix(in srgb, var(--ink) 78%, var(--ctl-color-black));
  }

  :host(:not([variant]), [variant="soft"]) .badge{
    background: color-mix(in srgb, var(--ink) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ink) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ink) 16%, var(--ctl-color-border));
  }

  :host([variant="outline"]) .badge{
    background: transparent;
    color: color-mix(in srgb, var(--ink) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ink) 35%, var(--ctl-color-border));
  }
</style>

<span class="badge" part="badge">
  <span class="icon" part="icon"><slot name="icon"></slot></span>
  <span class="label" part="label"><slot></slot></span>
</span>
`;

export class CtlBadge extends HTMLElement {
  static get observedAttributes() {
    return ["variant", "intent", "size", "pill"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "soft");
    if (!this.hasAttribute("intent")) this.setAttribute("intent", "neutral");
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");

    this._normalize();
  }

  attributeChangedCallback() {
    this._normalize();
  }

  _normalize() {
    const variant = this.getAttribute("variant") || "soft";
    if (!["soft", "solid", "outline"].includes(variant)) this.setAttribute("variant", "soft");

    const intent = this.getAttribute("intent") || "neutral";
    if (!["neutral", "info", "success", "warning", "danger"].includes(intent)) {
      this.setAttribute("intent", "neutral");
    }

    const size = this.getAttribute("size") || "md";
    if (!["sm", "md"].includes(size)) this.setAttribute("size", "md");
  }
}

customElements.define("ctl-badge", CtlBadge);
